import {
  EVENT_MEDIA_BUCKET,
  eventLinksValidationError,
  eventMediaStoragePath,
  eventMediaTypeFromInput,
  eventMediaUploadError,
  normalizeEventLinksDraft,
  type EventLinkDraft,
} from '@sixam/shared/event-assets-core';
import { fetch as expoFetch } from 'expo/fetch';
import { File } from 'expo-file-system';
import { Platform } from 'react-native';
import { supabase } from '@sixam/supabase/client';
import type { Database, Json } from '@sixam/shared/database.types';

export type EventLink = Database['public']['Tables']['event_links']['Row'];
export type EventMedia = Database['public']['Tables']['event_media']['Row'];

export type EventAssets = Readonly<{
  links: EventLink[];
  media: EventMedia[];
}>;

export type EventMediaUploadAsset = Readonly<{
  duration?: number | null;
  fileName?: string | null;
  fileSize?: number | null;
  height?: number | null;
  mimeType?: string | null;
  type?: string | null;
  uri: string;
  width?: number | null;
}>;

export const EMPTY_EVENT_ASSETS: EventAssets = {
  links: [],
  media: [],
};

export async function uploadLocalFileToStorage({
  bucket,
  contentType,
  storagePath,
  upsert = false,
  uri,
}: Readonly<{
  bucket: string;
  contentType: string;
  storagePath: string;
  upsert?: boolean;
  uri: string;
}>): Promise<number | null> {
  if (Platform.OS === 'web') {
    const blob = await globalThis.fetch(uri).then((response) => response.blob());
    const { error } = await supabase.storage.from(bucket).upload(storagePath, blob, {
      contentType,
      upsert,
    });
    if (error) throw error;
    return blob.size;
  }

  const file = new File(uri);
  const signedUpload = await supabase.storage
    .from(bucket)
    .createSignedUploadUrl(storagePath, { upsert });
  if (signedUpload.error) throw signedUpload.error;

  const response = await expoFetch(signedUpload.data.signedUrl, {
    body: file,
    headers: {
      'cache-control': 'max-age=3600',
      'content-type': contentType,
      'x-upsert': String(upsert),
    },
    method: 'PUT',
  });
  if (!response.ok) {
    throw new Error((await response.text()) || `Upload failed with status ${response.status}.`);
  }
  return file.size || null;
}

export async function fetchEventAssets(eventId: string): Promise<EventAssets> {
  const [linksResult, mediaResult] = await Promise.all([
    supabase
      .from('event_links')
      .select('*')
      .eq('event_id', eventId)
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true }),
    supabase
      .from('event_media')
      .select('*')
      .eq('event_id', eventId)
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true }),
  ]);

  if (linksResult.error) {
    throw linksResult.error;
  }
  if (mediaResult.error) {
    throw mediaResult.error;
  }

  return {
    links: linksResult.data ?? [],
    media: mediaResult.data ?? [],
  };
}

export async function fetchEventCoverImageUrls(
  eventIds: readonly string[]
): Promise<Map<string, string>> {
  const uniqueEventIds = [...new Set(eventIds)];
  if (uniqueEventIds.length === 0) {
    return new Map();
  }

  const { data, error } = await supabase
    .from('event_media')
    .select('event_id, storage_path')
    .in('event_id', uniqueEventIds)
    .eq('media_type', 'photo')
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true });

  if (error) {
    throw error;
  }

  const coverImages = new Map<string, string>();
  for (const media of data ?? []) {
    if (!coverImages.has(media.event_id)) {
      coverImages.set(media.event_id, eventMediaPublicUrl(media.storage_path));
    }
  }
  return coverImages;
}

export function eventMediaPublicUrl(storagePath: string): string {
  if (/^https?:\/\//i.test(storagePath)) {
    return storagePath;
  }

  const { data } = supabase.storage.from(EVENT_MEDIA_BUCKET).getPublicUrl(storagePath);
  return data.publicUrl;
}

export function storagePublicUrl(bucket: string, storagePath: string): string {
  if (/^https?:\/\//i.test(storagePath)) return storagePath;
  return supabase.storage.from(bucket).getPublicUrl(storagePath).data.publicUrl;
}

export async function saveEventLinks(
  eventId: string,
  tenantId: string,
  links: readonly EventLinkDraft[],
  actorId?: string | null
): Promise<void> {
  const validationError = eventLinksValidationError(links);
  if (validationError) {
    throw new Error(validationError);
  }

  const normalized = normalizeEventLinksDraft(links);
  const deleteResult = await supabase.from('event_links').delete().eq('event_id', eventId);
  if (deleteResult.error) {
    throw deleteResult.error;
  }

  if (normalized.length === 0) {
    return;
  }

  const { error } = await supabase.from('event_links').insert(
    normalized.map((link) => ({
      created_by: actorId ?? null,
      event_id: eventId,
      label: link.label,
      sort_order: link.sortOrder,
      tenant_id: tenantId,
      url: link.url,
    }))
  );

  if (error) {
    throw error;
  }
}

export async function deleteEventMedia(media: readonly EventMedia[]): Promise<void> {
  const ids = media.map((item) => item.id);
  if (ids.length === 0) {
    return;
  }

  const { error } = await supabase.from('event_media').delete().in('id', ids);
  if (error) {
    throw error;
  }

  const storagePaths = media
    .map((item) => item.storage_path)
    .filter((path) => !/^https?:\/\//i.test(path));

  if (storagePaths.length === 0) {
    return;
  }

  const { error: storageError } = await supabase.storage
    .from(EVENT_MEDIA_BUCKET)
    .remove(storagePaths);
  if (storageError) {
    throw storageError;
  }
}

export async function uploadEventMediaAsset({
  actorId,
  altText,
  asset,
  eventId,
  sequence,
  tenantId,
}: Readonly<{
  actorId?: string | null;
  altText?: string | null;
  asset: EventMediaUploadAsset;
  eventId: string;
  sequence: number;
  tenantId: string;
}>): Promise<EventMedia> {
  const validationError = eventMediaUploadError({
    duration: asset.duration == null ? null : asset.duration / 1000,
    mediaKind: asset.type,
    name: asset.fileName,
    size: asset.fileSize,
    type: asset.mimeType,
  });
  if (validationError) {
    throw new Error(validationError);
  }

  const mediaType = eventMediaTypeFromInput({
    mediaKind: asset.type,
    name: asset.fileName,
    type: asset.mimeType,
  });
  const storagePath = eventMediaStoragePath({
    eventId,
    mediaKind: asset.type,
    name: asset.fileName,
    sequence,
    tenantId,
    type: asset.mimeType,
  });
  const contentType = asset.mimeType ?? (mediaType === 'video' ? 'video/mp4' : 'image/jpeg');
  const uploadedSize = await uploadLocalFileToStorage({
    bucket: EVENT_MEDIA_BUCKET,
    contentType,
    storagePath,
    uri: asset.uri,
  });

  const metadata: Json = {
    duration: asset.duration ?? null,
    fileName: asset.fileName ?? null,
    height: asset.height ?? null,
    mimeType: asset.mimeType ?? contentType,
    size: asset.fileSize ?? uploadedSize,
    width: asset.width ?? null,
  };

  const { data, error } = await supabase
    .from('event_media')
    .insert({
      alt_text: altText ?? null,
      created_by: actorId ?? null,
      event_id: eventId,
      media_type: mediaType,
      metadata,
      sort_order: sequence,
      storage_path: storagePath,
      tenant_id: tenantId,
    })
    .select('*')
    .single();

  if (error) {
    throw error;
  }

  return data;
}
